import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// `accent` variant is a back-compat alias for `primary`.
const sharedBase = [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium',
    'transition-colors duration-[120ms]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:pointer-events-none',
].join(' ');

const buttonVariants = cva(sharedBase, {
    variants: {
        variant: {
            primary: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover',
            accent: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover',
            secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:opacity-90',
            outline: 'border border-input bg-background text-foreground shadow-xs hover:bg-muted hover:text-foreground',
            ghost: 'text-foreground hover:bg-muted hover:text-foreground',
            destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:opacity-90',
            link: 'text-primary underline-offset-4 hover:underline',
        },
        size: {
            sm: 'h-8 px-3 text-xs',
            md: 'h-8 px-3',
            lg: 'h-10 px-6 text-base',
            icon: 'h-9 w-9',
        },
    },
    defaultVariants: {
        variant: 'primary',
        size: 'md',
    },
});

export const Button = forwardRef(({ className, variant, size, loading = false, disabled, children, ...props }, ref) => {
    const isDisabled = !! (disabled || loading);
    return (
        <button
            ref={ref}
            className={cn(buttonVariants({ variant, size }), className)}
            disabled={isDisabled}
            aria-busy={loading || undefined}
            {...props}
        >
            {loading && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {children}
        </button>
    );
});

Button.displayName = 'Button';

export { buttonVariants };
